import { z } from 'zod';

export const managedSecretSurfaces = ['integration', 'tool', 'trigger', 'identity-provider'] as const;
export const managedSecretSurfaceSchema = z.enum(managedSecretSurfaces);
export type ManagedSecretSurface = z.infer<typeof managedSecretSurfaceSchema>;
export const DEFAULT_MANAGED_SECRET_MOUNT = 'secret';

const surfacePath: Record<ManagedSecretSurface, string> = {
  integration: 'integrations',
  tool: 'tools',
  trigger: 'triggers',
  'identity-provider': 'identity-providers',
};

export const secretPatchSchema = z.object({
  values: z.record(z.string().min(1)).default({}),
});
export type SecretPatch = z.infer<typeof secretPatchSchema>;

export function parseSecretPatch(values: Record<string, unknown>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(values)) {
    if (typeof value !== 'string') continue;
    const trimmed = value.trim();
    if (trimmed) out[key] = trimmed;
  }
  return out;
}

export function buildManagedSecretRef(input: {
  tenantId: string;
  surface: ManagedSecretSurface;
  resourceId: string;
  key: string;
  mount?: string;
}): string {
  const mount = sanitizePathSegment(input.mount ?? DEFAULT_MANAGED_SECRET_MOUNT);
  const tenantId = sanitizePathSegment(input.tenantId);
  const resourceId = sanitizePathSegment(input.resourceId);
  const key = sanitizeSecretKey(input.key);
  return `vault:${mount}/tenants/${tenantId}/${surfacePath[input.surface]}/${resourceId}#${key}`;
}

export function buildManagedSecretPath(input: {
  tenantId: string;
  surface: ManagedSecretSurface;
  resourceId: string;
  mount?: string;
}): string {
  const mount = sanitizePathSegment(input.mount ?? DEFAULT_MANAGED_SECRET_MOUNT);
  const tenantId = sanitizePathSegment(input.tenantId);
  const resourceId = sanitizePathSegment(input.resourceId);
  return `${mount}/tenants/${tenantId}/${surfacePath[input.surface]}/${resourceId}`;
}

function sanitizePathSegment(value: string): string {
  const trimmed = value.trim();
  if (!/^[a-zA-Z0-9_.-]+$/.test(trimmed)) {
    throw new Error(`Invalid managed secret path segment "${value}"`);
  }
  return trimmed;
}

function sanitizeSecretKey(value: string): string {
  const trimmed = value.trim();
  if (!/^[a-zA-Z0-9_.-]+$/.test(trimmed)) {
    throw new Error(`Invalid managed secret key "${value}"`);
  }
  return trimmed;
}
